import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';
import { AdminPartner } from '@platform/client/schemas';
import { useContext, useMemo, useState } from 'react';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminPartners } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { CreatePartnerModal } from './CreatePartnerModal';
import { EditPartnerModal } from './EditPartnerModal';

export const PartnersAdmin = () => {
  return (
    <AdminListPageWrapper title={'Partners'}>
      <PartnersAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<AdminPartner>();

const PartnersAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [createModalIsOpen, setCreateModalIsOpen] = useState(false);
  const [updatePartnerId, setUpdatePartnerId] = useState<number>();
  const {
    data: partners,
    isSuccess,
    isLoading,
    isError,
  } = AdminPartners.useGetAdminPartners(adminQueryOptionsV2, {
    query: {
      placeholderData: keepPreviousData,
    },
  });
  const { partners: partnersData = [], filteredCount = 0 } = partners || {};
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'ID',
          cell: (id) => id.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('logo', {
          header: 'Logo',
          cell: (name) => {
            return (
              <img
                src={name.getValue() || undefined}
                alt='Logo'
                className='h-10 w-10 object-contain'
              />
            );
          },
          enableColumnFilter: false,
          enableSorting: false,
          size: 25,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: (name) => name.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 350,
        }),
        columnHelper.accessor('active', {
          header: 'Active',
          cell: (active) => (active.getValue() ? 'Yes' : 'No'),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          meta: {
            filterVariant: 'date-select',
          },
          size: 25,
        }),
        columnHelper.accessor('updatedAt', {
          header: 'Updated At',
          cell: (updatedAt) => new Date(updatedAt.getValue()).toLocaleDateString(),
          meta: {
            filterVariant: 'date-select',
          },
          size: 25,
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => {
            return (
              <AdminActionsContainer
                actions={[
                  {
                    type: 'button',
                    text: 'Edit',
                    onPress: () => setUpdatePartnerId(row.original.id),
                  },
                ]}
              />
            );
          },
          size: 25,
        }),
      ] as ColumnDef<AdminPartner>[],
    [],
  );
  return (
    <>
      <AdminTable
        columns={columns}
        data={partnersData}
        filteredCount={filteredCount}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        enableFiltering={true}
        tableActions={[{ text: 'Create Partner', callback: () => setCreateModalIsOpen(true) }]}
      />
      {createModalIsOpen && <CreatePartnerModal onClose={() => setCreateModalIsOpen(false)} />}
      {updatePartnerId && (
        <EditPartnerModal
          partnerId={updatePartnerId}
          onClose={() => setUpdatePartnerId(undefined)}
        />
      )}
    </>
  );
};
